import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, request } from '@/lib/api/client'
import { projectKeys, useInvalidateProjects } from './api'
import type { BlockerInput, DocInput, InstallationInput, NoteInput, Project, StageInput } from './types'

/** Kurulumlar CRM kurulum işleridir (hesaba bağlı); proje kimliğiyle ilişkilendirilir. */
function installationBody(projectId: string, input: InstallationInput) {
  return {
    project_id: projectId,
    run_type: input.kind,
    title: input.kind === 'demo' ? 'Demo' : 'Kurulum',
    status: input.status,
    assignee_user_id: input.ownerId ?? '',
    scheduled_at: input.startDate ? `${input.startDate}T09:00:00Z` : '',
    target_date: input.targetDate,
  }
}

function useAccountOf(projectId: string) {
  const qc = useQueryClient()
  return (installationId: string) => qc.getQueryData<Project>(projectKeys.detail(projectId))?.installations.find((i) => i.id === installationId)?.accountId ?? ''
}

export function useSaveInstallation(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  const accountOf = useAccountOf(projectId)
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: InstallationInput }): Promise<{ id: string }> => {
      const body = installationBody(projectId, input)
      if (id) {
        await api.put(`/api/v1/accounts/${accountOf(id) || input.accountId}/deployments/${id}`, body)
        return { id }
      }
      const saved = await api.post<{ id: string }>(`/api/v1/accounts/${input.accountId}/deployments`, body)
      return { id: saved.id }
    },
    onSuccess: invalidate,
  })
}

export function useDeleteInstallation(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  const accountOf = useAccountOf(projectId)
  return useMutation({ mutationFn: (id: string) => api.del(`/api/v1/accounts/${accountOf(id)}/deployments/${id}`), onSuccess: invalidate })
}

export function useInstallationActions(projectId: string, installationId: string) {
  const invalidate = useInvalidateProjects(projectId)
  const accountOf = useAccountOf(projectId)
  const m = <A,>(fn: (arg: A) => Promise<unknown>) => ({ mutationFn: fn, onSuccess: invalidate })
  const base = () => `/api/v1/accounts/${accountOf(installationId)}/deployments/${installationId}`
  const qc = useQueryClient()
  const blockerResolved = (id: string) =>
    qc.getQueryData<Project>(projectKeys.detail(projectId))?.installations.find((i) => i.id === installationId)?.blockers.find((b) => b.id === id)?.resolved ?? false
  return {
    saveStage: useMutation(
      m(({ id, input }: { id?: string; input: StageInput }) => {
        const body = {
          title: input.name,
          description: input.description,
          assignee_user_id: input.ownerId ?? '',
          planned_date: input.plannedDate,
          completion: input.completion,
          status: input.status,
          task_ids: input.taskIds,
        }
        return id ? api.put(`${base()}/stages/${id}`, body) : api.post(`${base()}/stages`, body)
      }),
    ),
    deleteStage: useMutation(m((id: string) => api.del(`${base()}/stages/${id}`))),
    saveBlocker: useMutation(m((input: BlockerInput) => api.post(`${base()}/blockers`, { title: input.text, severity: input.severity, owner_user_id: input.ownerId ?? '', date: input.date }))),
    toggleBlocker: useMutation(m((id: string) => api.patch(`${base()}/blockers/${id}`, { is_resolved: !blockerResolved(id) }))),
    deleteBlocker: useMutation(m((id: string) => api.del(`${base()}/blockers/${id}`))),
    saveDoc: useMutation(
      m(({ id, input }: { id?: string; input: DocInput }) => {
        const body = { name: input.name, date: input.date, status: input.status }
        return id ? api.put(`${base()}/documents/${id}`, body) : api.post(`${base()}/documents`, body)
      }),
    ),
    deleteDoc: useMutation(m((id: string) => api.del(`${base()}/documents/${id}`))),
  }
}

export function useSaveNote(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: NoteInput }) =>
      id ? api.put(`/api/v1/projects/${projectId}/notes/${id}`, input) : api.post(`/api/v1/projects/${projectId}/notes`, input),
    onSuccess: invalidate,
  })
}

export function useDeleteNote(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({ mutationFn: (id: string) => api.del(`/api/v1/projects/${projectId}/notes/${id}`), onSuccess: invalidate })
}

export function useUploadFile(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({
    mutationFn: ({ file, taskId }: { file: File; taskId: string | null }) => {
      const form = new FormData()
      form.set('file', file)
      if (taskId) form.set('task_id', taskId)
      return request('POST', `/api/v1/projects/${projectId}/files`, { body: form })
    },
    onSuccess: invalidate,
  })
}

export function useDeleteFile(projectId: string) {
  const invalidate = useInvalidateProjects(projectId)
  return useMutation({ mutationFn: (id: string) => api.del(`/api/v1/projects/${projectId}/files/${id}`), onSuccess: invalidate })
}

export async function downloadFile(projectId: string, fileId: string, name = 'dosya') {
  return api.download(`/api/v1/projects/${projectId}/files/${fileId}`, name)
}
